import React from 'react';
import { Activity } from 'lucide-react';
import { PageShell } from '../components/layout/PageShell';
import { PageHero } from '../components/layout/PageHero';
import { getLightSectionTheme } from '../lib/sectionThemes';
import { CodeHealthTab } from '../components/admin/CodeHealthTab';
import EngineeringHealthTile from '../components/admin/EngineeringHealthTile';
import BuildInfoTile from '../components/admin/BuildInfoTile';
import CollapsibleSection from '../components/layout/CollapsibleSection';

/**
 * CodeHealthPage — `/admin/code-health`
 *
 * Top-level Administration nav entry hosting the Code Health
 * dashboard (lint/test/file-quality + history sparkline + manual
 * "Run health check now" trigger). Previously buried inside the
 * Platform Telemetry page's deep-dive tab strip; promoted to its
 * own surface so engineering / Ops can land here directly without
 * walking past unrelated telemetry tiles.
 *
 * The page is a thin shell — all data-loading + rendering lives in
 * the existing `<CodeHealthTab>` component which is also still
 * mounted under telemetry... no — split-only: telemetry no longer
 * embeds code health. The component is reused verbatim here.
 *
 * Permissions: Super Admin only (gated at the route level via
 * `<SuperAdminRoute>` in App.js — same gate that protects Data
 * Health, Email Suppressions, etc.).
 */
const CodeHealthPage = () => {
  const theme = getLightSectionTheme('Navy');
  return (
    <PageShell
      theme={theme}
      hero={(
        <PageHero
          title="Code Health"
          description="Automated lint, test, and file quality checks with daily monitoring + manual trigger."
          theme={theme}
          icon={Activity}
          testId="code-health-hero"
        />
      )}
    >
      <div className="px-4 md:px-8 lg:px-12 py-6 space-y-6" data-testid="code-health-page">
        {/* Build & runtime — moved here from Admin → Telemetry so
            engineering / Ops have a single page covering "what's
            shipped" + "what's failing" + "what's running heavy".
            Wrapped in a CollapsibleSection (default-open) so the
            primary score gauge below stays in view on dense
            screens once admins have already glanced at the build. */}
        <CollapsibleSection
          storageKey="codeHealth.engineering"
          eyebrow="Engineering"
          title="Build, code health & runtime"
          accent="text-indigo-600"
          testId="code-health-engineering-section"
          meta={<span className="text-[11px] text-slate-500">2 tiles</span>}
        >
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <EngineeringHealthTile />
            <BuildInfoTile />
          </div>
        </CollapsibleSection>

        <CodeHealthTab />
      </div>
    </PageShell>
  );
};

export default CodeHealthPage;
